import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ChevronUp, Megaphone, ClipboardCheck, CalendarDays, FileText, TrendingUp, ListTodo, UserCheck, Check, X, Square } from "lucide-react";
import { Panel, TitledPanel, EmptyState, IconTile, ArrowLink } from "@/portal/ui/Layout";
import { Badge, StatusBadge, DECISION_STATUS, DECISION_KIND } from "@/portal/ui/Badge";
import { Button } from "@/portal/ui/Button";
import { RespondModal, NEEDS_APPROVAL } from "@/portal/DecisionCard";
import { openDocument } from "@/portal/DocumentsTable";
import { fmtDateLong, fmtDateTime, fmtTime, dateBadge } from "@/portal/format";

export default function GovernanceTab({ portal, project, documents = [], onChanged }) {
  const [showResolved, setShowResolved] = useState(false);
  const [showOlder, setShowOlder] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const actions = (portal?.actions || []).map((a) => ({ ...a, project_id: project.id }));
  const pending = actions.filter((a) => a.status === "pending");
  const resolved = actions.filter((a) => a.status !== "pending");
  const updates = portal?.updates || [];
  const meetings = portal?.meetings || [];
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = meetings.filter((m) => m.kind !== "past" && (m.when || "").slice(0, 10) >= today).sort((a, b) => (a.when || "").localeCompare(b.when || ""));
  const past = meetings.filter((m) => !upcoming.includes(m));
  const latest = updates[0];

  if (!portal) return <EmptyState title="Guvernanța proiectului nu este încă activă" description="Echipa Brisk va publica deciziile, actualizările și ședințele aici." />;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-5 items-start">
      <div className="space-y-4">
        {pending.length === 0 && resolved.length === 0 ? (
          <EmptyState compact icon={ClipboardCheck} title="Nicio solicitare înregistrată" description="Solicitările de aprobare ale echipei vor apărea aici, cu impact pe cost și program." />
        ) : (
          <div className="space-y-3" data-testid="project-decisions">
            {pending.map((a) => <PendingRow key={a.id} a={a} onChanged={onChanged} />)}
            {pending.length === 0 && <div className="cp-panel px-4 py-3 text-[13.5px] text-cp-text-2 flex items-center gap-2"><Check className="h-4 w-4 text-cp-success" aria-hidden="true" /> Nicio solicitare în așteptare pe acest proiect.</div>}
            {resolved.length > 0 && (
              <div>
                <button type="button" onClick={() => setShowResolved((v) => !v)} aria-expanded={showResolved} data-testid="toggle-resolved"
                  className="inline-flex items-center gap-1.5 text-[13px] font-medium text-cp-text-2 hover:text-cp-text py-1">
                  {showResolved ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />} Istoric decizii ({resolved.length})
                </button>
                {showResolved && (
                  <Panel padded={false} className="mt-2 cp-rise">
                    <ul className="divide-y divide-cp-border">
                      {resolved.map((a) => (
                        <li key={a.id} className="px-4 py-3 flex items-start gap-3" data-testid={`decision-${a.id}`}>
                          <StatusBadge map={DECISION_STATUS} value={a.status} size="sm" className="mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0"><div className="text-[14px] font-medium text-cp-text">{a.title}</div><div className="cp-meta">{DECISION_KIND[a.kind] || a.kind} · {a.responded_by} · {fmtDateTime(a.responded_at)}{a.client_comment ? ` · „${a.client_comment}”` : ""}</div></div>
                          <Link to={`/portal/decizii?id=${a.id}`} className="cp-meta hover:text-cp-accent shrink-0">Registru</Link>
                        </li>
                      ))}
                    </ul>
                  </Panel>
                )}
              </div>
            )}
          </div>
        )}

        <Panel padded={false} data-testid="project-updates">
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-cp-border">
            <h3 className="cp-h3">Actualizări de la echipă</h3>
            {latest && <span className="cp-meta whitespace-nowrap">{fmtDateLong(latest.at)} <span className="mx-1">|</span> {latest.by}</span>}
          </div>
          {!latest ? <div className="px-5 py-8 text-center"><Megaphone className="h-8 w-8 mx-auto text-cp-text-3 mb-2" aria-hidden="true" /><p className="cp-body-sm">Nicio actualizare publicată încă.</p></div> : (
            <dl className="divide-y divide-cp-border">
              {latest.progress_this_week && <UpdateRow icon={TrendingUp} label="Progres" text={latest.progress_this_week} />}
              {latest.next_steps && <UpdateRow icon={ListTodo} label="Pași următori" text={latest.next_steps} />}
              {latest.client_actions && <UpdateRow icon={UserCheck} label="Așteptăm de la tine" text={latest.client_actions} warn />}
            </dl>
          )}
          {updates.length > 1 && (
            <div className="px-5 py-3 border-t border-cp-border">
              <button type="button" onClick={() => setShowOlder((v) => !v)} aria-expanded={showOlder} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-cp-text-2 hover:text-cp-text" data-testid="toggle-older-updates">
                {showOlder ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />} Actualizări anterioare ({updates.length - 1})
              </button>
              {showOlder && (
                <ol className="mt-3 space-y-4 cp-rise">
                  {updates.slice(1).map((u) => (
                    <li key={u.id} className="relative pl-4 border-l-2 border-cp-border">
                      <div className="cp-meta">{fmtDateLong(u.at)} · {u.by}</div>
                      {u.progress_this_week && <p className="text-[13.5px] text-cp-text-2 mt-1"><b className="font-semibold text-cp-text">Progres:</b> {u.progress_this_week}</p>}
                      {u.next_steps && <p className="text-[13.5px] text-cp-text-2 mt-0.5"><b className="font-semibold text-cp-text">Pași următori:</b> {u.next_steps}</p>}
                      {u.client_actions && <p className="text-[13.5px] text-cp-text-2 mt-0.5"><b className="font-semibold text-cp-warning">Așteptăm de la tine:</b> {u.client_actions}</p>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </Panel>
      </div>

      <div className="space-y-4" data-testid="project-meetings">
        {upcoming.length === 0 && past.length === 0 ? <EmptyState compact icon={CalendarDays} title="Nicio ședință programată" /> : (
          <>
            {upcoming.map((m, i) => <MeetingCard key={m.id} m={m} documents={documents} upcoming defaultOpen={i === 0} />)}
            {past.length > 0 && (
              <div>
                <button type="button" onClick={() => setShowPast((v) => !v)} aria-expanded={showPast} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-cp-text-2 hover:text-cp-text py-1" data-testid="toggle-past-meetings">
                  {showPast ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />} Ședințe anterioare ({past.length})
                </button>
                {showPast && <div className="space-y-3 mt-2 cp-rise">{past.map((m) => <MeetingCard key={m.id} m={m} documents={[]} />)}</div>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PendingRow({ a, onChanged }) {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const approval = NEEDS_APPROVAL.has(a.kind);
  return (
    <div className="cp-panel-amber" data-testid={`decision-${a.id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="w-full flex items-center gap-3 px-4 py-3.5 text-left" data-testid={`decision-toggle-${a.id}`}>
        <IconTile icon={FileText} tone="amber" />
        <div className="flex-1 min-w-0">
          <div className="text-[14.5px] font-semibold text-cp-text">{DECISION_KIND[a.kind] || a.title}</div>
          <div className="cp-meta mt-0.5 flex flex-wrap gap-x-2">
            {a.cost_impact && <span>Impact: {a.cost_impact}</span>}{a.cost_impact && a.schedule_impact && <span>|</span>}{a.schedule_impact && <span>{a.schedule_impact}</span>}
            {(a.cost_impact || a.schedule_impact) && a.deadline && <span>|</span>}{a.deadline && <span>Termen limită: {fmtDateLong(a.deadline)}</span>}
          </div>
        </div>
        <Badge tone="warning" size="sm">În așteptare</Badge>
        <ChevronRight className={`h-5 w-5 text-cp-text-3 transition-transform ${open ? "rotate-90" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 cp-rise">
          <p className="text-[14.5px] font-medium text-cp-text">{a.title}</p>
          {a.description && <p className="cp-body mt-1">{a.description}</p>}
          {a.recommendation && <p className="text-[13.5px] text-cp-text-2 mt-2"><span className="font-semibold text-cp-text">Recomandarea Brisk:</span> {a.recommendation}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            {approval ? (
              <>
                <Button size="sm" onClick={() => setModal("approved")} data-testid={`approve-${a.id}`}><Check /> Analizează și aprobă</Button>
                <Button size="sm" variant="outline" onClick={() => setModal("rejected")} data-testid={`reject-${a.id}`}><X /> Respinge</Button>
              </>
            ) : <Button size="sm" onClick={() => setModal("acknowledged")} data-testid={`ack-${a.id}`}><Check /> Confirmă că am luat la cunoștință</Button>}
            <Link to={`/portal/decizii?id=${a.id}`} className="inline-flex items-center h-8 px-2 text-[13px] font-medium text-cp-accent hover:underline ml-auto">Vezi în Decizii</Link>
          </div>
          <RespondModal action={a} decision={modal} onClose={() => setModal(null)} onDone={onChanged} />
        </div>
      )}
    </div>
  );
}

function UpdateRow({ icon: Icon, label, text, warn }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[170px_1fr] gap-1 sm:gap-4 px-5 py-3">
      <dt className={`flex items-center gap-2 text-[13.5px] font-medium ${warn ? "text-cp-warning" : "text-cp-text"}`}><Icon className="h-4 w-4 text-cp-text-3" aria-hidden="true" />{label}</dt>
      <dd className="text-[13.5px] text-cp-text-2 leading-relaxed">{text}</dd>
    </div>
  );
}

const lines = (s) => String(s || "").split(/\n+/).map((x) => x.replace(/^\s*[-•\d.)]+\s*/, "").trim()).filter(Boolean);

function MeetingCard({ m, documents, upcoming, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const db = dateBadge(m.when);
  const agenda = lines(m.agenda); const actions = lines(m.action_items);
  const docs = documents.slice(0, 3);
  return (
    <TitledPanel testid={`meeting-${m.id}`} className="!p-0 overflow-hidden">
      <div className="flex items-start gap-3 p-5">
        <div className="h-12 w-12 rounded-[var(--cp-radius)] bg-cp-accent-soft text-cp-accent grid place-items-center shrink-0 leading-none" aria-hidden="true">
          <span className="cp-serif text-[20px]">{db.day}</span><span className="text-[9px] font-bold tracking-wide mt-0.5">{db.mon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[16px] font-semibold text-cp-text leading-snug">{m.title}</div>
          <div className="cp-meta mt-0.5">{m.when ? <>{fmtDateLong(m.when)}, {fmtTime(m.when)}</> : "Dată de stabilit"}</div>
        </div>
        {upcoming ? <Badge tone="accent" size="sm">Urmează</Badge> : <Badge tone="neutral" size="sm">Încheiată</Badge>}
      </div>
      {open && (
        <div className="px-5 pb-5 grid grid-cols-1 md:grid-cols-2 gap-5 cp-rise">
          <div>
            {agenda.length > 0 && <><div className="cp-h4 mb-1.5">Agendă</div><ol className="list-decimal pl-5 space-y-1 text-[13px] text-cp-text-2">{agenda.map((l, i) => <li key={i}>{l}</li>)}</ol></>}
            {actions.length > 0 && <><div className="cp-h4 mt-4 mb-1.5">Acțiuni de urmărit</div><ul className="space-y-1.5 text-[13px] text-cp-text-2">{actions.map((l, i) => <li key={i} className="flex items-start gap-2"><Square className="h-3.5 w-3.5 mt-0.5 text-cp-text-3 shrink-0" aria-hidden="true" />{l}</li>)}</ul></>}
            {m.minutes && <><div className="cp-h4 mt-4 mb-1.5">Minută</div><p className="text-[13px] text-cp-text-2 whitespace-pre-line">{m.minutes}</p></>}
            {m.decisions && <><div className="cp-h4 mt-4 mb-1.5">Decizii</div><p className="text-[13px] text-cp-text-2 whitespace-pre-line">{m.decisions}</p></>}
            {!agenda.length && !actions.length && !m.minutes && !m.decisions && <p className="cp-body-sm">Agenda ședinței va fi publicată de echipa Brisk.</p>}
          </div>
          {docs.length > 0 && (
            <div>
              <div className="cp-h4 mb-1.5">Documente relevante</div>
              <ul className="divide-y divide-cp-border">
                {docs.map((d) => (
                  <li key={d.id}><button type="button" onClick={() => openDocument(d)} className="w-full flex items-center gap-2 py-2 text-left text-[13px] text-cp-text hover:text-cp-accent" data-testid={`meeting-doc-${d.id}`}><FileText className="h-4 w-4 text-cp-text-3 shrink-0" aria-hidden="true" /><span className="truncate flex-1">{d.name}</span><ChevronRight className="h-4 w-4 text-cp-text-3" aria-hidden="true" /></button></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <div className="px-5 py-3 border-t border-cp-border flex justify-end">
        <ArrowLink onClick={() => setOpen((v) => !v)} testid={`meeting-toggle-${m.id}`}>{open ? "Restrânge" : "Vezi detaliile"}</ArrowLink>
      </div>
    </TitledPanel>
  );
}
